import React from 'react';

import {LoadingOutlined} from '@ant-design/icons';
import {Typography} from 'antd';
import type {TextProps} from 'antd/es/typography/Text';
import styled from 'styled-components';

import {Secret} from 'components/Secret';

const {Text} = Typography;

export type Property = {
  label: React.ReactNode;
  value: React.ReactNode;
  hide?: boolean;
  secret?: boolean;
} & TextProps;

type Props = {
  properties: Array<Property>;
  emptyText?: string;
  omitEmpty?: boolean;
  loading?: boolean;
  labelWidth?: number;
  style?: React.CSSProperties;
  className?: string;
};

const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;
const ListItem = styled.li`
  display: flex;
  padding-top: 4px;
  padding-bottom: 4px;
  flex-direction: row;
`;

const Label = styled.div<{labelWidth: number}>`
  color: rgba(0, 0, 0, 0.55);
  flex: 0 0 50%;
  min-width: 180px;
  max-width: ${(props) => props.labelWidth}px;
`;
const Value = styled.div`
  color: rgba(0, 0, 0, 0.75);
  padding-right: 4px;
  padding-left: 4px;
  white-space: pre-wrap;
`;

export const PropertyList: React.FC<Props> = ({
  properties,
  omitEmpty = false,
  emptyText = 'No data',
  loading = false,
  labelWidth = 220,
  style,
  className
}) => {
  const data = omitEmpty
    ? properties.filter((prop) => !prop.hide && prop.value !== null && prop.value !== undefined)
    : properties;

  const renderValue = ({label, value, secret, ...textProps}: Property) => {
    if (loading) {
      return (
        <Text type="secondary">
          <LoadingOutlined />
        </Text>
      );
    }
    if (value === null || value === undefined) {
      let labelText = 'value';
      if (typeof label === 'string') {
        labelText = label.toLowerCase();
      } else if (
        React.isValidElement<{children?: React.ReactNode}>(label) &&
        typeof label.props.children === 'string'
      ) {
        labelText = label.props.children.toLowerCase();
      }
      return <Text type="secondary">{`No ${labelText} provided`}</Text>;
    }
    if (secret) {
      return (
        <Secret {...textProps}>
          {typeof value === 'string' ? value : String(value as unknown)}
        </Secret>
      );
    }
    return <Text {...textProps}>{value}</Text>;
  };

  return (
    <List style={style} className={className}>
      {data.length > 0 ? (
        data.map((prop, i) => (
          <ListItem key={i}>
            <Label labelWidth={labelWidth}>{prop.label}</Label>
            <Value>{renderValue(prop)}</Value>
          </ListItem>
        ))
      ) : (
        <ListItem>
          <Label labelWidth={labelWidth}>{emptyText}</Label>
        </ListItem>
      )}
    </List>
  );
};
